import { _notNil } from '@/littledash';
import type { AgentSessionV1, SendBundleValidationResult, SendInputFailureResult } from '@/model/Agent.model';

/**
 * Narrow an execution result to a {@link SendBundleValidationResult} by shape: the validation result
 * is the one carrying both `per_domain` and `cross_domain`. Returns null for any other shape.
 */
export const asBundleValidationResult = (value: unknown): SendBundleValidationResult | null => {
  if (typeof value === 'object' && value !== null && 'per_domain' in value && 'cross_domain' in value) {
    return value as SendBundleValidationResult;
  }
  return null;
};

/**
 * Find the validation result by shape, not array position: a SEND session carries a detection and a
 * validation execution, returned newest-first, so a position-based lookup would read the wrong one.
 */
export const findBundleValidationResult = (session: AgentSessionV1 | null): SendBundleValidationResult | null => {
  for (const execution of session?.executions ?? []) {
    const validation = asBundleValidationResult(execution?.result);
    if (_notNil(validation)) {
      return validation;
    }
  }
  return null;
};

/**
 * Narrow an execution result to a {@link SendInputFailureResult} by its declared `result_type`. A run
 * stopped this way read nothing it could check, so it must never be mistaken for an empty result.
 */
export const asInputFailureResult = (value: unknown): SendInputFailureResult | null => {
  if (typeof value === 'object' && value !== null && 'result_type' in value && value.result_type === 'input_failure') {
    return value as SendInputFailureResult;
  }
  return null;
};

/**
 * Find an input-failure result on either execution of the session: detection and validation can each
 * stop on a file that cannot be read.
 */
export const findInputFailureResult = (session: AgentSessionV1 | null): SendInputFailureResult | null => {
  for (const execution of session?.executions ?? []) {
    const failure = asInputFailureResult(execution?.result);
    if (_notNil(failure)) {
      return failure;
    }
  }
  return null;
};
